import {APP_INITIALIZER, Injector, NgModule} from '@angular/core';
import {Router, RouterModule, Routes} from '@angular/router';
import {SharedRoutesService} from './shared-routes.service';
import {DynamicRouteService} from './routing/dynamic-route.service';
import {ROUTE_CONTEXTS} from './routing/route-context';
import {registerBaseCategorySlugs} from './routing/base-category-registry';
import {AddListingUrlCanonicalizerService} from './add-new-item/add-listing-url-canonicalizer.service';

// Bootstrap adds `{ path: '', children: [...] }` including redirect-to-home and lazy modules.
// Do not add a top-level redirect here — Angular resolves redirectTo as a sibling route, so
// `redirectTo: 'home'` would fail because `home` lives under the bootstrap child tree.
let platformRoutes: Routes = [
  {path: 'admin', loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule)},
];

export function initializeRoutes(
  dynamicRouteService: DynamicRouteService,
  sharedRoutes: SharedRoutesService,
  addListingUrlCanonicalizer: AddListingUrlCanonicalizerService,
  injector: Injector,
) {
  return () => dynamicRouteService.loadBootstrap().toPromise()
    .then(bootstrap => {
      for (const context of ROUTE_CONTEXTS) {
        const routes = bootstrap?.[context];
        if (!routes?.length) {
          continue;
        }
        if (context === 'app') {
          platformRoutes.push(...routes);
        } else {
          sharedRoutes.set(context, routes);
        }
      }
    })
    // The routing bootstrap only emits "" + ":id" at the top level, so the real
    // category slugs come from /categories. Registering them lets URL parsing
    // distinguish a base category (e.g. "test") from a profile id generically.
    .then(() => dynamicRouteService.loadBaseCategories().toPromise())
    .then(slugs => registerBaseCategorySlugs(slugs || []))
    .then(() => {
      injector.get(Router).resetConfig(platformRoutes);
      addListingUrlCanonicalizer.init();
    })
    // Never let routing initialization reject — that would abort APP_INITIALIZER
    // and leave the SPA with no routes at all.
    .catch(err => {
      console.error('Routing initialization failed; continuing with static routes only', err);
      injector.get(Router).resetConfig(platformRoutes);
    });
}

@NgModule({
  imports: [RouterModule.forRoot(platformRoutes)],
  exports: [RouterModule],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeRoutes,
      deps: [DynamicRouteService, SharedRoutesService, AddListingUrlCanonicalizerService, Injector],
      multi: true,
    },
  ],
})
export class AppRoutingModule {}
